import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/use-user-role";
import { CoachCalendar } from "@/components/CoachCalendar";
import { CoachAnalytics } from "@/components/CoachAnalytics";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, BarChart3, ArrowLeft, User } from "lucide-react";
import { toast } from "sonner";

interface Booking {
  id: string;
  booking_datetime: string; // UTC ISO timestamp
  booking_timezone?: string;
  duration_minutes: number;
  status: string;
  price: number;
  user_id: string;
  video_conference_link?: string;
  profiles?: {
    full_name: string;
    email: string;
  };
}

interface CoachProfile {
  full_name: string;
  email: string;
}

const AdminCoachView = () => {
  const navigate = useNavigate();
  const { coachId } = useParams();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [coachProfile, setCoachProfile] = useState<CoachProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!roleLoading && !isAdmin) {
      toast.error("Access denied. This page is for admins only.");
      navigate("/");
    }
  }, [isAdmin, roleLoading, navigate]);

  useEffect(() => {
    if (isAdmin && coachId) {
      fetchCoachData();
    }
  }, [isAdmin, coachId]);

  const fetchCoachData = async () => {
    try {
      // Fetch coach profile
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("full_name, email")
        .eq("id", coachId)
        .single();

      if (profileError) throw profileError;
      setCoachProfile(profileData);

      // Fetch bookings for this coach (assigned to them or unassigned)
      const { data: bookingsData, error: bookingsError } = await supabase
        .from("bookings")
        .select(`
          *,
          profiles:user_id (
            full_name,
            email
          )
        `)
        .or(`coach_id.eq.${coachId},coach_id.is.null`)
        .order("booking_datetime", { ascending: true });

      if (bookingsError) throw bookingsError;
      setBookings(bookingsData || []);
    } catch (error) {
      console.error("Error fetching coach data:", error);
      toast.error("Failed to load coach data");
    } finally {
      setLoading(false);
    }
  };

  if (roleLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-accent/20">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin")}
            className="mb-2"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Admin Portal
          </Button>
          <div className="flex items-center gap-3">
            <User className="h-6 w-6 text-muted-foreground" />
            <div>
              <h1 className="text-2xl font-bold">
                {coachProfile?.full_name || "Coach"}'s Calendar
              </h1>
              <p className="text-sm text-muted-foreground">{coachProfile?.email}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Tabs defaultValue="calendar" className="space-y-6">
          <TabsList>
            <TabsTrigger value="calendar">
              <Calendar className="w-4 h-4 mr-2" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="analytics">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="calendar">
            <CoachCalendar bookings={bookings} onUpdate={fetchCoachData} isAdminView={true} coachId={coachId} />
          </TabsContent>

          <TabsContent value="analytics">
            <CoachAnalytics bookings={bookings} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminCoachView;
